import React, { useState, useEffect, useMemo } from 'react';
import { User, Shield, Building2, Globe, Terminal, Settings as SettingsIcon } from 'lucide-react';
import PasswordSettings from '@/components/features/settings/PasswordSettings';
import OrganizationUsers from '@/components/features/settings/OrganizationUsers';
import OrganizationDetails from '@/components/features/settings/OrganizationDetails';
import DomainVerification from '@/components/features/settings/DomainVerification';
import { useUser, useOrganization } from "@clerk/clerk-react";
// @ts-ignore
import type { TabId, TabItem } from "../../lib/api/types";

const Settings: React.FC = () => {
    const { user, isLoaded: isUserLoaded } = useUser();
    const { organization, isLoaded: isOrgLoaded } = useOrganization();
    const [activeTab, setActiveTab] = useState<TabId>("users");

    const currentOrgId = organization?.id;

    const currentMembership = useMemo(() => {
        if (!user || !currentOrgId) return null;
        const memberships = (user as any)?.organizationMemberships ?? [];
        return memberships.find((m: any) => m?.organization?.id === currentOrgId);
    }, [user, currentOrgId]);

    const roleName = (currentMembership as any)?.roleName || (currentMembership as any)?.role_name || "";
    const role = (currentMembership as any)?.role || "";

    const isAdmin = useMemo(() => {
        const rn = String(roleName).toLowerCase();
        const r = String(role).toLowerCase();
        return rn === "admin" || r.includes("admin");
    }, [roleName, role]);

    const allTabs = useMemo(() => [
        { id: "users" as const, label: "Organization Users", icon: User },
        { id: "details" as const, label: "Organization Details", icon: Building2 },
        { id: "domains" as const, label: "Domain Verification", icon: Globe },
        { id: "password" as const, label: "Password Settings", icon: Shield },
    ], []);

    const tabs = useMemo(() => {
        return isAdmin ? allTabs : allTabs.filter((t) => t.id === "password");
    }, [isAdmin, allTabs]);

    useEffect(() => {
        if (!isUserLoaded || !isOrgLoaded) return;
        const allowedIds = new Set(tabs.map((t) => t.id));
        if (!allowedIds.has(activeTab)) {
            setActiveTab(tabs[0]?.id ?? "password");
        }
    }, [isUserLoaded, isOrgLoaded, tabs, activeTab]);

    if (!isUserLoaded || !isOrgLoaded) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center font-mono">
                <div className="flex items-center gap-3 text-slate-500 uppercase tracking-widest text-xs">
                    <Terminal className="h-4 w-4 animate-pulse text-[#00FFFF]" />
                    Syncing_System_Preferences...
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-slate-300 font-mono p-8 selection:bg-[#00FFFF]/30 selection:text-white">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Tactical Header */}
                <div className="space-y-2 border-b border-[#1A1D24] pb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-2 bg-[#00FFFF]/10 border border-[#00FFFF]/20 rounded-lg">
                                <SettingsIcon className="w-6 h-6 text-[#00FFFF]" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white tracking-tight uppercase">
                                    Settings
                                </h2>
                                <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] mt-1">
                                    Manage organization users, details, domain verification, and security settings
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="flex items-center justify-between">
                    <nav className="flex space-x-1 p-1 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl shadow-inner">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        flex items-center gap-2.5 py-2.5 px-6 rounded-lg font-bold text-[11px] transition-all uppercase tracking-widest
                                        ${isActive
                                            ? 'bg-[#50A970] text-black shadow-[0_0_15px_rgba(0,255,255,0.4)]'
                                            : 'text-slate-500 hover:text-white hover:bg-[#12151C]'
                                        }
                                    `}
                                >
                                    <Icon className={`h-3.5 w-3.5 ${isActive ? 'text-black' : 'text-slate-500'}`} />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Content Panel */}
                <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden relative">
                    {/* Top scanning line decoration */}
                    <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#00FFFF]/30 to-transparent"></div>

                    <div className="p-8">
                        {activeTab === "users" && isAdmin && <OrganizationUsers />}
                        {activeTab === "details" && isAdmin && <OrganizationDetails />}
                        {activeTab === "domains" && isAdmin && <DomainVerification />}
                        {activeTab === "password" && <PasswordSettings />}
                    </div>
                </div>

                {/* Footer System Status */}
                <div className="flex items-center justify-between px-2 opacity-50">
                    <div className="flex gap-4 text-[9px] uppercase tracking-widest font-bold text-slate-600">
                        <span>PRV_LEVEL: {isAdmin ? 'LEVEL_0_ROOT' : 'LEVEL_1_USER'}</span>
                        <span>ORG_ID: {currentOrgId?.slice(0, 12)}...</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-widest font-bold text-slate-600">
                        Ver_2.0.4_BETA
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Settings;